import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import NewEvent from "../../pages/portal/new-event/NewEvent.jsx";
import PostedEvents from "../../pages/portal/posted-events/PostedEvents.jsx";
import { useUser } from "../UserContext/UserProvider.jsx";
import VenuesDashboard from "../../components/VenuesDashboard/VenuesDashboard.jsx";
import axiosInstance from "../../api/axiosInstance.js";
import { backendUrl } from "../../api/constants.js";
import Templates from "../../pages/portal/templates/Templates.jsx";
import BusinessTools from "../../pages/portal/calendar/Calendar.jsx";
import RosterDashboard from "../../pages/portal/roster/RosterDashboard.jsx";
import BookingsInfo from "../../pages/portal/roster/BookingsInfo.jsx";
import RosterPage, { DEFAULT_ROSTER_VIEW, ROSTER_VIEWS } from "../../pages/roster/RosterPage.jsx";
import Festivals from "../../pages/portal/Festivals.jsx";
import PortalMenu from "../../components/PortalMenu/PortalMenu.jsx";
import { getMyLocation as getLocationApi, getVenues } from "../../api/events.js";
import { getCurrentLocation } from "../../util/dataUtils.jsx";
import { modal } from "../../util/useModal.jsx";
import { useMediaQuery } from "../../util/useMediaQuery.js";
import { ROSTER_FEATURE_ENABLED } from "../../config/features.js";
import {
  HiOutlineCalendarDays,
  HiOutlineDocumentDuplicate,
  HiOutlineListBullet,
  HiSparkles,
} from "react-icons/hi2";

const PortalContext = createContext({});

// shown for dashboard features an account can't use yet (Portal.jsx tab gate + locked menu items)
export const getRestrictedFeatureCopy = (tab) => {
  const label = tab?.label || "this feature";

  return {
    title: label,
    body: `${label} is part of what in-yc offers. your current account does not have access to this feature yet, but we keep it visible here so you can see what's available. contact us if you want access or think this should be enabled for your account.`,
  };
};

const BOOKINGS_TABS = ["calendar", "rosters", "status"];
const newyork = { lat: 40.7128, lng: -74.006 };
const POSTED_EVENTS_CALENDAR_PREFS_KEY = "posted_events_calendar_preferences";

const initialVenueDashboardData = {
  tickets: undefined,
  userVenues: undefined,
  venues: undefined,
  loading: false,
  loaded: false,
  lastFetchedAt: null,
  locationKey: null,
  error: null,
};

const getInitialPostedEventsViewMode = () => {
  try {
    const stored = JSON.parse(
      localStorage.getItem(POSTED_EVENTS_CALENDAR_PREFS_KEY) || "{}"
    );
    return ["list", "calendar"].includes(stored.viewMode) ? stored.viewMode : "calendar";
  } catch (error) {
    return "calendar";
  }
};
// export const allowedUsers = ["test","jeridgooding","jerid-inyc"];

export const PortalProvider = ({ children }) => {
  const [userLocation, setUserLocation] = useState({
    lat: newyork.lat,
    lng: newyork.lng,
  });
  const [locationLoading, setLocationLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(null);
  const { user } = useUser();
  // the performer side of rosters (opportunities / lists / browse); same audience as the old nav icon
  const showArtistRosters = ROSTER_FEATURE_ENABLED && user?.accountType === "artist";
  const [currentTab, setCurrentTab] = useState();
  const [editEvent, setEditEvent] = useState();
  const [prefillEventData, setPrefillEventData] = useState(null); // For "post similar event" functionality
  const [targetEventPortalTab, setTargetEventPortalTab] = useState(null);
  const [targetVenuePortalTab, setTargetVenuePortalTab] = useState(null);
  const [currentVenuePortalTab, setCurrentVenuePortalTab] = useState("new_venue");
  const [templatePickerRequestId, setTemplatePickerRequestId] = useState(0);
  const [guidedPostingRequestId, setGuidedPostingRequestId] = useState(0);
  const [postedEventsViewMode, setPostedEventsViewModeState] = useState(
    getInitialPostedEventsViewMode
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);
  const [venueDashboardData, setVenueDashboardData] = useState(initialVenueDashboardData);
  const venueDashboardDataRef = useRef(initialVenueDashboardData);
  const venueDashboardRequestRef = useRef(null);
  let location = useLocation();
  const medium = useMediaQuery("md");

  useEffect(() => {
    venueDashboardDataRef.current = venueDashboardData;
  }, [venueDashboardData]);

  const getVenueDashboardLocationKey = useCallback((marker = userLocation) => {
    if (!marker?.lat || !marker?.lng) return "no-location";
    return `${Number(marker.lat).toFixed(5)},${Number(marker.lng).toFixed(5)}`;
  }, [userLocation]);

  const normalizeUserVenues = useCallback((venues = []) =>
    venues.map((venue) => ({ ...venue, me: true })), []);

  const setVenueDashboardTickets = useCallback((updater) => {
    setVenueDashboardData((prev) => ({
      ...prev,
      tickets: typeof updater === "function" ? updater(prev.tickets || []) : updater,
    }));
  }, []);

  const refreshVenueDashboardUserVenues = useCallback(async () => {
    const { data } = await axiosInstance.get(`${backendUrl}/business/venues`);
    const userVenues = normalizeUserVenues(data?.data || []);
    setVenueDashboardData((prev) => ({
      ...prev,
      userVenues,
    }));
    return userVenues;
  }, [normalizeUserVenues]);

  const refreshVenueDashboardVenues = useCallback(async (marker = userLocation) => {
    if (!marker?.lat || !marker?.lng) return [];

    const venuesData = await getVenues(marker);
    const venues = venuesData?.data?.data || [];
    setVenueDashboardData((prev) => ({
      ...prev,
      venues,
    }));
    return venues;
  }, [userLocation]);

  const fetchVenueDashboardData = useCallback(async (opts = {}) => {
    const { force = false, location: requestedLocation } = opts;
    const marker = requestedLocation || userLocation;
    const locationKey = getVenueDashboardLocationKey(marker);
    const current = venueDashboardDataRef.current;

    if (!force && current.loaded && current.locationKey === locationKey) {
      return current;
    }

    if (!force && venueDashboardRequestRef.current) {
      return venueDashboardRequestRef.current;
    }

    setVenueDashboardData((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    const request = (async () => {
      try {
        const [ticketsResponse, userVenuesResponse, venuesResponse] = await Promise.all([
          axiosInstance.get(`${backendUrl}/business/tickets`, {
            params: {
              status: "all",
              includeResolved: true,
            },
          }),
          axiosInstance.get(`${backendUrl}/business/venues`),
          marker?.lat && marker?.lng ? getVenues(marker) : Promise.resolve(null),
        ]);

        const nextData = {
          tickets: ticketsResponse?.data?.data || [],
          userVenues: normalizeUserVenues(userVenuesResponse?.data?.data || []),
          venues: venuesResponse?.data?.data || [],
          loading: false,
          loaded: true,
          lastFetchedAt: Date.now(),
          locationKey,
          error: null,
        };

        setVenueDashboardData(nextData);
        return nextData;
      } catch (error) {
        console.log(error);
        setVenueDashboardData((prev) => ({
          ...prev,
          loading: false,
          loaded: prev.loaded,
          error,
        }));
        return venueDashboardDataRef.current;
      } finally {
        if (venueDashboardRequestRef.current === request) {
          venueDashboardRequestRef.current = null;
        }
      }
    })();

    venueDashboardRequestRef.current = request;
    return request;
  }, [getVenueDashboardLocationKey, normalizeUserVenues, userLocation]);

  const getVisibleTabsList = () => tabsList.filter((tb) => !tb.hiddenFromMenu);

  const setPostedEventsViewMode = useCallback((mode) => {
    if (!["list", "calendar"].includes(mode)) return;
    setPostedEventsViewModeState(mode);
  }, []);

  const setCurrentTabAndUrl = useCallback((tab, opts = {}) => {
    setCurrentTab(tab);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', tab);
      if (opts.clearEdit) {
        newParams.delete('edit');
      }
      return newParams;
    });
  }, [setSearchParams]);

  const setTabFromUrl = useCallback((tab) => {
    setCurrentTab(tab);
  }, []);

  useEffect(() => {
    if (searchParams.get("edit") && userLocation) {
      // console.log(searchParams.get("edit"));
      const fetchEditEvent = async () => {
        const fetchedEditEvent = await getLocationApi(
          searchParams.get("edit"),
          userLocation
        );
        // console.log(fetchedEditEvent);
        setEditEvent(fetchedEditEvent);
      };
      fetchEditEvent();
    }
  }, [searchParams, userLocation]);

  const rostersViewParam = searchParams.get("rostersView");
  const currentRosterView = ROSTER_VIEWS.includes(rostersViewParam) ? rostersViewParam : DEFAULT_ROSTER_VIEW;
  const bookingsTabParam = searchParams.get("bookingsTab");
  const currentBookingsTab = BOOKINGS_TABS.includes(bookingsTabParam) ? bookingsTabParam : "calendar";
  // which sub-heading is underlined when several share one tab (rosters views, bookings calendar/rosters)
  const selectedPortalMenuItemId =
    currentTab === "rosters"
      ? `roster-${currentRosterView}`
      : currentTab === "bookings"
      ? `bookings-${currentBookingsTab}`
      : undefined;
  // which option chip is highlighted (posted: list | calendar)
  const selectedPortalMenuActionId =
    currentTab === "posted-events"
      ? { list: "posted-list-action", calendar: "posted-calendar-action" }[postedEventsViewMode]
      : undefined;

  const handlePortalMenuItemClick = useCallback((item) => {
    if (item.locked) {
      const copy = getRestrictedFeatureCopy({ label: item.lockedLabel || item.label });
      modal(() => (
        <div className="max-w-[34rem]">
          <p className="font-['Primary-Bold'] text-kit-md">{copy.title}</p>
          {item.lockedContent ? (
            <div className="mt-kit-8">{item.lockedContent()}</div>
          ) : (
            <p className="mt-kit-8 text-kit-sm leading-[1.45]">{copy.body}</p>
          )}
        </div>
      ));
      return;
    }

    if (item.bookingsTab) {
      setCurrentTab("bookings");
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("tab", "bookings");
        newParams.set("bookingsTab", item.bookingsTab);
        return newParams;
      });
      return;
    }

    if (item.rostersView) {
      setCurrentTab("rosters");
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("tab", "rosters");
        newParams.set("rostersView", item.rostersView);
        return newParams;
      });
      return;
    }

    if (item.action === "templates") {
      setEditEvent(null);
      setPrefillEventData(null);
      setCurrentTabAndUrl("new-event", { clearEdit: true });
      setTemplatePickerRequestId((prev) => prev + 1);
      return;
    }

    if (item.action === "guided-posting") {
      setEditEvent(null);
      setPrefillEventData(null);
      setCurrentTabAndUrl("new-event", { clearEdit: true });
      setGuidedPostingRequestId((prev) => prev + 1);
      return;
    }

    if (item.venueTab) {
      setTargetVenuePortalTab(item.venueTab);
      setCurrentVenuePortalTab(item.venueTab);
      setCurrentTabAndUrl("venues");
      return;
    }

    if (item.postedViewMode) {
      setPostedEventsViewMode(item.postedViewMode);
      setCurrentTabAndUrl("posted-events");
      return;
    }

    if (item.tab) {
      if (item.clearEdit) {
        setEditEvent(null);
        setPrefillEventData(null);
      }
      setCurrentTabAndUrl(item.tab, { clearEdit: item.clearEdit });
    }
  }, [setCurrentTabAndUrl, setPostedEventsViewMode, setSearchParams]);

  const handleMoreOptionsClick = async () => {
    if (medium) return;
    const result = await modal(
      ({ proceed }) => {
        return (
          <div className="mb-kit-16 py-kit-16 border-[1px] rounded-[--inyc-input-border-radius] bg-[--inyc-primary-light] border-[--inyc-modal-border-color] min-w-[min(90vw,300px)]">
            <PortalMenu
              groups={getVisiblePortalMenuGroups()}
              currentTab={currentTab}
              selectedItemId={selectedPortalMenuItemId}
              selectedActionId={selectedPortalMenuActionId}
              onSelect={(item) => {
                handlePortalMenuItemClick(item);
              }}
            />
          </div>
        );
      },
      {
        containerClassName: "!items-end",
        raw: true,
        enableCloseOnBackdropClick: true,
        onClose: () => {
          setMoreOptionsOpen(false);
        },
      }
    );
  };

  useEffect(() => {
    const checkPermission = async () => {
      try {
        const { data } = await axiosInstance.get(
          `${backendUrl}/user/permissions?${tabsList
            .map((tb) => `&&permissions[]=${tb.permission}`)
            .join("")}`
        );
        // console.log(data);
        if (data.error) {
          console.log(data.error);
        } else {
          setHasPermission(data);

          const hashQuery = window.location.hash.split("?")[1] || "";
          const urlTab = new URLSearchParams(hashQuery).get("tab");
          const firstTab = tabsList.find((tb) => data[tb.permission] === true);
          if (firstTab) {
            setCurrentTab((prev) => prev || urlTab || firstTab.index);
          }
        }
      } catch (error) {
        console.log(error);
      }
    };
    checkPermission();
  }, [user]);

  const tabsList = [
    {
      label: "new event",
      index: "new-event",
      id: "new_event",
      permission: "ACCESS_INFLUENCER_DASHBOARD",
      element: ({ key }) => <NewEvent key={key} userLocation={userLocation} />,
    },
    {
      label: "posted events",
      index: "posted-events",
      id: "posted_events",
      permission: "ACCESS_INFLUENCER_DASHBOARD",
      element: ({ key }) => <PostedEvents key={key} />,
    },
    {
      label: "templates",
      index: "templates",
      id: "templates",
      permission: "ACCESS_INFLUENCER_DASHBOARD",
      element: ({ key }) => <Templates key={key} />,
    },
    {
      label: "venues",
      index: "venues",
      id: "venues",
      permission: "ACCESS_BUSINESS_DASHBOARD",
      element: () => (
        <VenuesDashboard
          userLocation={userLocation}
          targetVenuePortalTab={targetVenuePortalTab}
          setTargetVenuePortalTab={setTargetVenuePortalTab}
        />
      ),
    },
    {
      label: "calendar",
      index: "calendar",
      id: "calendar",
      permission: "ACCESS_CALENDAR_DASHBOARD",
      element: () => <BusinessTools />,
    },
    {
      label: "festivals",
      index: "festivals",
      id: "festivals",
      permission: "ACCESS_BUSINESS_DASHBOARD",
      element: () => <Festivals />,
    },
    {
      label: "rosters",
      index: "rosters",
      id: "rosters",
      permission: "ACCESS_INFLUENCER_DASHBOARD",
      hiddenFromMenu: !showArtistRosters,
      element: () => <RosterPage />,
    },
    {
      label: "bookings",
      index: "bookings",
      id: "bookings",
      permission: "MANAGE_ROSTER",
      hiddenFromMenu: !ROSTER_FEATURE_ENABLED,
      element: () => <RosterDashboard />,
      // no-access screen explains what bookings is instead of the generic message
      restrictedContent: () => <BookingsInfo />,
    },
  ];

  const portalMenuGroups = [
    {
      id: "events",
      label: "events",
      items: [
        {
          id: "new-event",
          label: "new",
          tab: "new-event",
          clearEdit: true,
          inlineActions: [
            {
              id: "new-template-action",
              label: "start from a template",
              action: "templates",
              icon: <HiOutlineDocumentDuplicate size={15} />,
            },
            {
              id: "new-guided-posting-action",
              label: "guided posting",
              action: "guided-posting",
              icon: <HiSparkles size={15} />,
            },
          ],
        },
        {
          id: "posted-events",
          label: "posted",
          tab: "posted-events",
          notificationScope: "dashboard:posted-events",
          inlineActions: [
            {
              id: "posted-list-action",
              label: "list",
              postedViewMode: "list",
              icon: <HiOutlineListBullet size={15} />,
            },
            {
              id: "posted-calendar-action",
              label: "calendar",
              postedViewMode: "calendar",
              icon: <HiOutlineCalendarDays size={15} />,
            },
          ],
        },
        { id: "templates-page", label: "templates", tab: "templates" },
        { id: "calendar", label: "calendar sync", tab: "calendar" },
      ],
    },
    {
      id: "venues",
      label: "venues",
      items: [
        { id: "new-venue", label: "new", tab: "venues", venueTab: "new_venue" },
        { id: "pending-venues", label: "status", tab: "venues", venueTab: "pending_venues" },
        { id: "claimed-venues", label: "claimed", tab: "venues", venueTab: "claimed_venues" },
      ],
    },
    // single-page sections: hideItems drops the sub-row so the label isn't repeated under the header
    {
      id: "festivals",
      label: "festivals",
      hideItems: true,
      items: [{ id: "festivals", label: "festivals", tab: "festivals" }],
    },
    // business accounts: bookings → calendar ⌄ (list chip) · rosters they manage.
    // artists: the same slot reads "rosters" → the performer side, plus a locked calendar.
    showArtistRosters
      ? {
          id: "bookings",
          label: "rosters",
          items: [
            {
              id: "roster-opportunities",
              label: "opportunities",
              tab: "rosters",
              rostersView: "opportunities",
              notificationScope: "roster:opportunities",
            },
            {
              id: "roster-lists",
              label: "my lists",
              tab: "rosters",
              rostersView: "lists",
              notificationScope: "roster:memberships",
            },
            { id: "roster-browse", label: "browse", tab: "rosters", rostersView: "browse" },
            {
              id: "bookings-calendar-locked",
              label: "calendar",
              lockedLabel: "bookings calendar",
              locked: true,
              lockedContent: () => <BookingsInfo />,
            },
          ],
        }
      : {
          id: "bookings",
          label: "bookings",
          items: [
            {
              id: "bookings-calendar",
              label: "dates",
              tab: "bookings",
              bookingsTab: "calendar",
            },
            {
              id: "bookings-rosters",
              label: "rosters",
              tab: "bookings",
              bookingsTab: "rosters",
              notificationScope: "dashboard:bookings",
            },
            // booking events as a list with status filters (all / active / pending / rejected)
            { id: "bookings-status", label: "status", tab: "bookings", bookingsTab: "status" },
          ],
        },
  ];

  const getVisiblePortalMenuGroups = () => {
    const tabByIndex = new Map(tabsList.map((tab) => [tab.index, tab]));

    return portalMenuGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          if (item.hidden) return false;
          const tab = tabByIndex.get(item.tab);
          return !tab || !tab.hiddenFromMenu;
        }),
      }))
      .filter((group) => group.items.length > 0);
  };

  useEffect(() => {
    getLocation();
  }, []);

  const getLocation = () => {
    try {
      getCurrentLocation({
        setLocation: setUserLocation,
        default: {
          latitude: newyork.lat,
          longitude: newyork.lng,
        },
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLocationLoading(false);
    }
  };

  const navigateToEditEvent = (eventId, targetTab = null) => {
    setTargetEventPortalTab(targetTab);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', 'new-event');
      newParams.set('edit', eventId);
      return newParams;
    });
    setCurrentTab('new-event');
  };

  const value = {
    userLocation,
    setUserLocation,
    locationLoading,
    tabsList,
    visibleTabsList: getVisibleTabsList(),
    portalMenuGroups: getVisiblePortalMenuGroups(),
    hasPermission,
    moreOptionsOpen,
    setMoreOptionsOpen,
    setHasPermission,
    handlePortalClick: setCurrentTabAndUrl,
    handlePortalMenuItemClick,
    handleMoreOptionsClick,
    currentTab,
    setTabFromUrl,
    setCurrentTab: setCurrentTabAndUrl,
    editEvent,
    setEditEvent,
    prefillEventData,
    setPrefillEventData,
    navigateToEditEvent,
    templatePickerRequestId,
    guidedPostingRequestId,
    postedEventsViewMode,
    setPostedEventsViewMode,
    targetEventPortalTab,
    setTargetEventPortalTab,
    targetVenuePortalTab,
    setTargetVenuePortalTab,
    currentVenuePortalTab,
    setCurrentVenuePortalTab,
    selectedPortalMenuItemId,
    selectedPortalMenuActionId,
    venueDashboardData,
    fetchVenueDashboardData,
    setVenueDashboardTickets,
    refreshVenueDashboardVenues,
    refreshVenueDashboardUserVenues,
  };

  return (
    <PortalContext.Provider value={value}>{children}</PortalContext.Provider>
  );
};

export const usePortalContext = () => {
  const context = useContext(PortalContext);
  if (context === undefined) {
    throw new Error("usePortalContext must be used within a PortalProvider");
  }
  return context;
};
